import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import useFetch from "../../hooks/useFetch";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass, faStar, faLocationDot, faIdCard } from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";

import { profilePicUrl } from "./psImageUrls";
import { ui, EmptyState, ErrorState, EntityThumb } from "./psShared";

export default function PSProfiles() {
  const authState = useSelector((state) => state.authReducer);
const token = authState?.token;
const [fetchData] = useFetch();

const [search, setSearch] = useState("");
const [profiles, setProfiles] = useState([]);
const [loading, setLoading] = useState(false);
const [error, setError] = useState(null);
const requestSeq = useRef(0);

  const load = async () => {
    const mySeq = ++requestSeq.current;

    try {
        setLoading(true);
        setError(null);
        const requestConfig = {
                url: "/ps/profiles",
                method: "post",
                data: { 
                  searchKey: search
                },
                headers: { Authorization: token },
            };

        const response = await fetchData(requestConfig, { silent: true });

        if (mySeq !== requestSeq.current) return;

        setProfiles(response?.data || response || []);
    } catch (e) {
        if (mySeq !== requestSeq.current) return;
        setError({ status: e?.status, message: e?.message });
    } finally {
        if (mySeq !== requestSeq.current) return;
        setLoading(false);
    }
};

  useEffect(() => {
    if (!token) return;

    const t = setTimeout(() => {
        load();
    }, 300);

    return () => clearTimeout(t);
}, [search, token]);

  const waLink = (p) => {
    const phone = p.phone?.replace(/\D/g, "");
    if (!phone) return null;
    const name = p.userId?.name ? ` ${p.userId.name}` : "";
    const text = encodeURIComponent(`Hi${name}, saw your profile on PehchaanSetu. Would like to connect.`);
    return `https://wa.me/91${phone}?text=${text}`;
  };

  return (
    <div className="p-4 max-w-3xl mx-auto">
      <div className="relative">
        <FontAwesomeIcon icon={faMagnifyingGlass} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-[12px] text-white/30" />
        <input
          type="text"
          placeholder="Search by name, skill, or business…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className={`${ui.input} pl-9`}
        />
      </div>

      <div className="mt-4 grid gap-3">
        {loading &&
          Array.from({ length: 3 }).map((_, i) => <div key={i} className={`${ui.card} h-20 animate-pulse`} />)}

        {!loading && error && (
          <ErrorState
            message={
              error.status === 404
                ? "The profile directory couldn't be reached right now."
                : error.message || "Couldn't load profiles."
            }
            onRetry={load}
          />
        )}

        {!loading &&
          !error &&
          profiles.map((p) => {
            const wa = waLink(p);
            const pic = profilePicUrl(p.userId?.think5whyscode, p.updatedAt);
            return (
              <div key={p._id} className={`${ui.card} flex gap-3.5 p-3.5`}>
                <EntityThumb src={pic} name={p.businessName || p.userId?.name} shape="circle" size="md" />

                <div className="min-w-0 flex-1">
                  <h3 className="truncate text-[13px] lg:text-base font-bold text-white">
                    {p.businessName || p.userId?.name || "Unnamed"}
                  </h3>
                  <p className="text-[11px] lg:text-sm text-white/50">{p.skill}</p>
                  {(p.userId?.area || p.userId?.city) && (
                    <p className="flex items-center gap-1 text-[10px] lg:text-xs text-white/35">
                      <FontAwesomeIcon icon={faLocationDot} className="text-[9px]" />
                      {p.userId?.area ? `${p.userId.area}, ` : ""}{p.userId?.city}
                    </p>
                  )}
                  <div className="mt-1 flex items-center gap-3 text-[10px] lg:text-xs text-white/40">
                    <span className="flex items-center gap-1">
                      <FontAwesomeIcon icon={faStar} className="text-amber-300 text-[10px]" />
                      {p.rating || "New"}
                    </span>
                    <span>{p.jobsDone || 0} jobs</span>
                  </div>
                </div>

                {wa && (
                  <a
                    href={wa}
                    target="_blank"
                    rel="noreferrer"
                    className="flex shrink-0 items-center gap-1.5 self-center rounded-full bg-emerald-500/90 px-3 py-1.5 text-[10px] lg:text-xs font-semibold text-white hover:bg-emerald-500"
                  >
                    <FontAwesomeIcon icon={faWhatsapp} />
                    Chat
                  </a>
                )}
              </div>
            );
          })}

        {!loading && !error && profiles.length === 0 && (
          <EmptyState
            icon={faIdCard}
            title={`No profiles found${search ? ` for "${search}"` : ""}.`}
            hint="Try a different name, skill, or business."
          />
        )}
      </div>
    </div>
  );
}
